import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ScrollToTopButton from '@/components/base/ScrollToTopButton';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-4xl text-foreground-400 mb-4 block" />
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const chapters = Array.from({ length: comic.chapters }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(new Date(comic.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
  }));

  return (
    <>
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'ComicSeries',
              name: comic.title,
              url: `https://www.manhwa-read.com/comic/${comic.slug}`,
              author: {
                '@type': 'Person',
                name: comic.author,
              },
              description: comic.description,
              inLanguage: 'zh-CN',
              genre: comic.tags,
              numberOfIssues: comic.chapters,
              image: comic.cover,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: comic.rating,
                reviewCount: Math.floor(comic.views / 100),
              },
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.manhwa-read.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category?.name || '分类',
                  item: `https://www.manhwa-read.com/category/${comic.category}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: comic.title,
                },
              ],
            },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category?.name || '分类', href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        <article className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-4">作者：{comic.author}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1 bg-[oklch(var(--accent-100))] text-[oklch(var(--accent-700))] text-xs px-3 py-1 rounded-full whitespace-nowrap">
                    <i className="ri-star-fill text-xs" />
                    {comic.rating}
                  </span>
                  <span className="text-xs text-foreground-600 whitespace-nowrap">
                    <i className="ri-eye-line mr-1" />
                    {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                  </span>
                  <span className="text-xs text-foreground-600 whitespace-nowrap">
                    <i className="ri-file-list-line mr-1" />
                    共 {comic.chapters} 话
                  </span>
                  <span className={`inline-block text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-[oklch(var(--primary-100))] text-[oklch(var(--primary-700))]'
                      : 'bg-background-200 text-foreground-600'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block text-xs text-[oklch(var(--secondary-700))] bg-[oklch(var(--secondary-100))] px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-950 mb-2">漫画简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="inline-flex items-center justify-center gap-2 bg-[oklch(var(--primary-500))] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[oklch(var(--primary-600))] transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-play-circle-line" />
                    开始阅读 第1话
                  </button>
                  <button className="inline-flex items-center justify-center gap-2 bg-background-100 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-background-200 transition-all whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line" />
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-10">
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">章节列表</h2>
              <div className="bg-background-100 rounded-lg divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
                {chapters.map((ch) => (
                  <div
                    key={ch.number}
                    className="flex items-center justify-between px-4 py-3 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-600 whitespace-nowrap">
                        第{ch.number}话
                      </span>
                      <span className="text-sm text-foreground-950">{ch.title}</span>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{ch.date}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="mt-10">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} {...c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTopButton />
    </>
  );
}